/**
 * Debounced global notification when an API returns 401/403 so we can show
 * one "sign in again" dialog instead of many overlapping errors.
 *
 * Also set when Cognito refresh fails (see useTokenRefresh / AuthContext) — must not
 * use window.location to /login or the dialog never mounts.
 */
export const SESSION_EXPIRED_UI_KEY = "sagelyn:sessionExpiredPrompt";

const DEBOUNCE_MS = 2500;
let lastEmitAt = 0;

function markSessionExpiredPrompt(): void {
  try {
    sessionStorage.setItem(SESSION_EXPIRED_UI_KEY, "1");
  } catch {
    /* private mode / quota */
  }
}

export function clearSessionExpiredPrompt(): void {
  try {
    sessionStorage.removeItem(SESSION_EXPIRED_UI_KEY);
  } catch {
    /* ignore */
  }
}

const FORBIDDEN_DEBOUNCE_MS = 3000;
let lastForbiddenEmitAt = 0;

export function notifySessionUnauthorized(status: number): void {
  if (status === 403) {
    // 403 = signed in, but no access to this Brand or action (SGL-588). Signing
    // in again cannot fix that, so it must never open the sign-in dialog.
    const now = Date.now();
    if (now - lastForbiddenEmitAt < FORBIDDEN_DEBOUNCE_MS) return;
    lastForbiddenEmitAt = now;
    window.dispatchEvent(new CustomEvent("app:access-forbidden"));
    return;
  }
  if (status !== 401) {
    return;
  }
  const now = Date.now();
  if (now - lastEmitAt < DEBOUNCE_MS) {
    return;
  }
  lastEmitAt = now;
  markSessionExpiredPrompt();
  window.dispatchEvent(
    new CustomEvent("app:session-unauthorized", { detail: { status } })
  );
}

const CREDIT_DEBOUNCE_MS = 3000;
let lastCreditEmitAt = 0;

export function notifyCreditInsufficient(): void {
  const now = Date.now();
  if (now - lastCreditEmitAt < CREDIT_DEBOUNCE_MS) return;
  lastCreditEmitAt = now;
  window.dispatchEvent(new CustomEvent("app:credit-insufficient"));
}

/** Routes where we should not show the session dialog (auth flows, public pages). */
export const PATHS_WITHOUT_SESSION_MODAL: string[] = [
  "/login",
  "/forgot-password",
  "/verify-email",
  "/new-password",
];

export function shouldSuppressSessionModal(pathname: string): boolean {
  return PATHS_WITHOUT_SESSION_MODAL.some(
    (p) => pathname === p || pathname.startsWith(`${p}/`)
  );
}
